import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import {
  HelpCircle, Search, BookOpen, MessageCircle, Mail, Send,
  ChevronDown, ChevronUp, Users, Smartphone, Lock, Box, Sparkles, Globe
} from 'lucide-react'

const Container = styled.div`
  padding: 32px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 22px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`

const HeroSearch = styled.div`
  background: linear-gradient(135deg, rgba(124,58,237,0.10) 0%, rgba(37,99,235,0.10) 100%);
  border: 1px solid rgba(124,58,237,0.20);
  border-radius: 16px;
  padding: 28px 24px;
  margin-bottom: 22px;
  text-align: center;
`

const HeroTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 8px 0;
`

const HeroDesc = styled.p`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 18px 0;
`

const SearchInputWrap = styled.div`
  position: relative;
  max-width: 520px;
  margin: 0 auto;

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.textMuted};
    pointer-events: none;
  }
  input {
    width: 100%;
    padding: 14px 18px 14px 46px;
    border: 1px solid ${props => props.theme.border};
    border-radius: 12px;
    background: ${props => props.theme.cardBackground};
    font-size: 14px;
    color: ${props => props.theme.textPrimary};

    &:focus {
      outline: none;
      border-color: #7c3aed;
      box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
    }
    &::placeholder { color: ${props => props.theme.textMuted}; }
  }
`

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 22px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 14px;
  padding: 20px;
`

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 14px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`

const TopicBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  background: ${props => props.theme.cardBackground};
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    border-color: #7c3aed;
    background: rgba(124,58,237,0.04);
    transform: translateY(-1px);
  }

  .topic-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(124,58,237,0.10);
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .topic-label {
    font-size: 13px;
    font-weight: 600;
    color: ${props => props.theme.textPrimary};
  }
`

const FaqItem = styled.div`
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
  background: ${props => props.theme.cardBackground};
`

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};

  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
`

const FaqAnswer = styled.div`
  padding: 0 16px 14px 16px;
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  line-height: 1.6;
  border-top: 1px solid ${props => props.theme.borderLight};
  padding-top: 12px;
`

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`

const ContactCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  background: ${props => props.theme.cardBackground};
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    border-color: #7c3aed;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(124,58,237,0.10);
  }

  .icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(124,58,237,0.10);
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .label {
    font-size: 14px;
    font-weight: 700;
    color: ${props => props.theme.textPrimary};
    margin-bottom: 2px;
  }
  .desc {
    font-size: 12px;
    color: ${props => props.theme.textSecondary};
  }
`

const TOPICS = [
  { key: 'getting-started', label: '시작하기', Icon: Sparkles },
  { key: 'create-quiz', label: '퀴즈 만들기', Icon: Box },
  { key: 'play-game', label: '게임 플레이', Icon: Users },
  { key: 'mobile-app', label: '모바일 앱', Icon: Smartphone },
  { key: 'account', label: '계정/로그인', Icon: Lock },
  { key: 'tutorial', label: '사용 가이드', Icon: BookOpen },
]

const FAQS = [
  {
    q: 'AR 대탈출은 무엇인가요?',
    a: 'AR 대탈출은 증강현실(AR) 기술을 활용한 학습형 퀴즈 플랫폼입니다. 직접 퀴즈를 만들고, 친구와 함께 AR 환경에서 문제를 풀어보세요.',
  },
  {
    q: '퀴즈는 어떻게 만드나요?',
    a: '왼쪽 메뉴의 "만들기"에서 4단계(기본 정보 → 오브젝트 등록 → 문제 만들기 → 배포하기)를 따라 누구나 쉽게 퀴즈를 만들 수 있습니다.',
  },
  {
    q: '비공개 퀴즈는 어떻게 설정하나요?',
    a: '퀴즈 만들기 1단계 "기본 정보" 화면에서 비공개 옵션을 활성화하면 됩니다. 비공개 퀴즈는 본인의 보관함에서만 접근할 수 있습니다.',
  },
  {
    q: '모바일 앱은 어디서 다운로드하나요?',
    a: '랜딩/로그인 페이지에 표시되는 QR 코드를 스캔하시거나, Google Play 스토어에서 "AR 대탈출"을 검색하시면 됩니다.',
  },
  {
    q: '비밀번호를 잊어버렸어요.',
    a: '로그인 페이지의 "비밀번호를 잊으셨나요?" 링크를 클릭하시면 가입한 이메일로 재설정 링크가 발송됩니다.',
  },
  {
    q: '함께 플레이하려면 어떻게 하나요?',
    a: '퀴즈 상세 페이지에서 "방 만들기"를 누르고, 생성된 6자리 방 코드를 친구에게 공유하면 함께 플레이할 수 있습니다.',
  },
]

export default function HelpPage() {
  const { theme } = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const [openFaq, setOpenFaq] = useState(null)

  const filteredFaqs = FAQS.filter(f =>
    !searchTerm.trim()
    || f.q.toLowerCase().includes(searchTerm.toLowerCase())
    || f.a.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Container theme={theme}>
      <PageHeader>
        <Title theme={theme}>도움말 및 지원</Title>
        <Subtitle theme={theme}>AR 대탈출 사용 중 궁금한 점이 있으시면 아래에서 답을 찾아보세요.</Subtitle>
      </PageHeader>

      <HeroSearch theme={theme}>
        <HeroTitle theme={theme}>무엇을 도와드릴까요?</HeroTitle>
        <HeroDesc theme={theme}>키워드로 빠르게 찾아보세요.</HeroDesc>
        <SearchInputWrap theme={theme}>
          <Search size={18} />
          <input
            type="text"
            placeholder="궁금한 점을 검색해보세요..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInputWrap>
      </HeroSearch>

      <SectionGrid>
        <Card theme={theme}>
          <CardTitle theme={theme}>
            <BookOpen size={18} color="#7c3aed" />
            도움말 카테고리
          </CardTitle>
          <TopicGrid>
            {TOPICS.map(t => {
              const Icon = t.Icon
              return (
                <TopicBtn
                  key={t.key}
                  theme={theme}
                  onClick={() => alert(`"${t.label}" 카테고리 페이지 준비 중입니다.`)}
                >
                  <span className="topic-icon"><Icon size={16} /></span>
                  <span className="topic-label">{t.label}</span>
                </TopicBtn>
              )
            })}
          </TopicGrid>
        </Card>

        <Card theme={theme}>
          <CardTitle theme={theme}>
            <MessageCircle size={18} color="#7c3aed" />
            문의하기
          </CardTitle>
          <ContactGrid>
            <ContactCard theme={theme} onClick={() => window.location.href = 'mailto:support@antsnest.co.kr'}>
              <span className="icon-wrap"><Mail size={18} /></span>
              <div>
                <div className="label">이메일 문의</div>
                <div className="desc">support@antsnest.co.kr</div>
              </div>
            </ContactCard>
            <ContactCard theme={theme} onClick={() => alert('실시간 채팅 상담은 준비 중입니다.')}>
              <span className="icon-wrap"><Send size={18} /></span>
              <div>
                <div className="label">실시간 채팅</div>
                <div className="desc">평일 09:00 ~ 18:00</div>
              </div>
            </ContactCard>
            <ContactCard theme={theme} onClick={() => window.open('https://antsnest.co.kr', '_blank', 'noopener')}>
              <span className="icon-wrap"><Globe size={18} /></span>
              <div>
                <div className="label">공식 홈페이지</div>
                <div className="desc">antsnest.co.kr</div>
              </div>
            </ContactCard>
          </ContactGrid>
        </Card>
      </SectionGrid>

      <Card theme={theme}>
        <CardTitle theme={theme}>
          <HelpCircle size={18} color="#7c3aed" />
          자주 묻는 질문 (FAQ)
        </CardTitle>
        {filteredFaqs.length === 0 ? (
          <div style={{
            padding: '32px 16px',
            textAlign: 'center',
            color: theme.textSecondary,
            fontSize: 13,
          }}>
            검색 결과가 없습니다.
          </div>
        ) : filteredFaqs.map((faq, idx) => (
          <FaqItem key={idx} theme={theme}>
            <FaqQuestion
              theme={theme}
              onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
            >
              <span>Q. {faq.q}</span>
              {openFaq === idx
                ? <ChevronUp size={18} color={theme.textMuted} />
                : <ChevronDown size={18} color={theme.textMuted} />}
            </FaqQuestion>
            {openFaq === idx && (
              <FaqAnswer theme={theme}>{faq.a}</FaqAnswer>
            )}
          </FaqItem>
        ))}
      </Card>
    </Container>
  )
}
